import { getNativeBridgeFunction } from 'libs/shared-ui/src/commonUtils/nativeBridge';
import { ConnectError } from '@bufbuild/connect';
import {
  IClassAndSubjectSelected,
  Loader,
  callTeacherEventsApi,
  clearLocalStorageKeys,
  deleteOfflineAccessKeyFromCache,
  deserify,
  getDeviceType,
  getLocalStorage,
  getMediaBasePath,
  setLocalStorage,
  storeOfflineAccessKeyInCache,
  useCommonServiceClientContext,
  useNotificationPermission,
} from '@geneo2-web/shared-ui';
import {
  DeviceEnum,
  ProfileRolesEnum,
} from '@protos/user_management/ums.db_pb';
import {
  TeacherLoginResponseType,
  TeachClassSubjects,
} from '@protos/user_management/ums.login.apis_pb';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';
import { useAppSelector,
  useDeserifiedSelector } from '../../../reduxStore/reduxHooks';
import {
  HOME,
  HOMEWORK_MANAGE,
  SCREEN_RESOLVER,
  TEACH_CHAPTER_SELECTION,
} from '../../../routeHandling/RoutesNomenclature';
import { getSubjectEnumFromStringEnum } from '../../../utils/commonUtils';
import { resetAnalyticsState } from '../../Analytics/reducer/analytics.slice';
import { resetAssessmentFlowState } from '../../Assessment/reducer/assessmentFlow.slice';
import { aiBookCloseEvent } from '../../Home/home.events';
import {
  resetHomeDashboardState,
  setClassAndSubjectInfo,
} from '../../Home/reducer/homeDashboard.slice';
import { aiHomeworkCloseEvent } from '../../Homework/homework.events';
import { resetManageHWState } from '../../ManageHomework/reducer/manageHomework.slice';
import {
  resetTeachState,
  setSelectedResourceSessionId,
} from '../../Teach/reducer/teach.slice';
import {
  aiChapterCloseEvent,
  aiLessonCloseEvent,
  aiTopicCloseEvent,
} from '../../Teach/teach.events';
import { aiLoginEvent, aiLogoutEvent, interactionEvent } from '../auth.events';
import {
  resetAuthState,
  setIsLoggingOut,
  setIsMCBUser,
  setSsoData,
  setSsoRedirectionUrl,
  setUserInfo,
} from '../reducer/auth.slice';
import { MCBErrorScreen } from '../MCBLogin/components/MCBErrorScreen';

export const GenericSsoLogin = () => {
  const { external_client } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const deviceType = getDeviceType();
  const userId = getLocalStorage('userId');
  const deviceId = getLocalStorage('deviceID');
  const fcmToken = getLocalStorage('fcmToken');
  const uid = new URLSearchParams(location.search).get('uid') || undefined;
  const token = new URLSearchParams(location.search).get('token') || undefined;
  const mode = new URLSearchParams(location.search).get('mode') || undefined;
  const subjectEnum =
    new URLSearchParams(location.search).get('subject') || undefined;
  // Optional explicit tenant override for multi-tenant SSO providers (see
  // validateUserSsoLoginV4). Most providers don't need this — the backend
  // falls back to the school_group's configured tenantId when omitted.
  const tenantId =
    new URLSearchParams(location.search).get('tenant_id') || undefined;

  let auth = getLocalStorage('auth');
  if (auth === 'true' || auth === 'false') {
    auth = JSON.parse(auth);
  }
  const commonServiceClientContext = useCommonServiceClientContext();
  const { isOffline } = useDownloadContext();
  const {
    UmsSsoAPIServiceV1ClientWithStatusCodeHandler,
    UMSLoginAPIServiceV1ClientWithStatusCodeHandler,
    UmsUtilityAPIServiceV1ClientWithStatusCodeHandler,
  } = commonServiceClientContext;
  const { user_info, sso_data } = deserify(
    useAppSelector((state) => state.auth)
  );
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  const { chapter_resources, selected_topic_info, selected_lessons_info } =
    deserify(useAppSelector((state) => state.teach));
  const { selected_assessment_id } = deserify(
    useAppSelector((state) => state.manageHomework)
  );

  const { requestPermission } = useNotificationPermission('teacher', isOffline);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  // ----------------------------------------------------

  const notificationAPI = async () => {
    if (!userId) {
      console.log('User ID not found.');
      return;
    }
    if (isOffline) return;

    try {
      await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.saveDeviceInfo({
        deviceId: deviceId ? BigInt(deviceId) : undefined,
        profileId: BigInt(userId),
        role: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
        deviceType: DeviceEnum.DEVICE_TYPE_WEB,
        deviceToken: fcmToken?.toString(),
        isActive: false,
      });
      await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.saveDeviceInfo({
        deviceToken: fcmToken?.toString(),
        topic: `${String(userId)}_${ProfileRolesEnum.PROFILE_ROLE_TEACHER}`,
        subscribeTopic: false,
      });
    } catch (err) {
      console.error('Error in Notification API:', err);
    }
  };

  // ----------------------------------------------------

  const commonAiCloseEvents = async () => {
    await aiLessonCloseEvent({
      lessonId: selected_lessons_info?.lessonId,
      isOffline: isOffline,
    });
    await aiTopicCloseEvent({
      topicId: selected_topic_info?.topicId,
      isOffline: isOffline,
    });
    await aiChapterCloseEvent({
      chapterId: chapter_resources?.chapterId,
      isOffline: isOffline,
    });
    await aiBookCloseEvent({
      bookId: class_subject_info?.bookId,
      isOffline: isOffline,
    });
    await aiHomeworkCloseEvent({
      homeworkId: selected_assessment_id,
      isOffline: isOffline,
    });
    await aiLogoutEvent({ isOffline: isOffline });
    dispatch(setSelectedResourceSessionId(undefined));
    !isOffline && (await callTeacherEventsApi(commonServiceClientContext));
  };

  // ----------------------------------------------------

  const handleLogout = async () => {
    try {
      dispatch(setIsLoggingOut(true));
      await commonAiCloseEvents();
      // if (fcmToken) {
      //   await notificationAPI();
      // }
      await UMSLoginAPIServiceV1ClientWithStatusCodeHandler.logout({
        profileId: getLocalStorage('userId'),
        role: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
      });
    } catch (err) {
      console.log(err);
      dispatch(setIsLoggingOut(false));
    } finally {
      dispatch(resetAuthState());
      dispatch(resetHomeDashboardState());
      dispatch(resetAssessmentFlowState());
      dispatch(resetManageHWState());
      dispatch(resetTeachState());
      dispatch(resetAnalyticsState());
      clearLocalStorageKeys();
      deleteOfflineAccessKeyFromCache();
    }
  };

  // ----------------------------------------------------

  function pushUserDetailsToAndroid(uToken: string, userID: string): void {
    try {
      if (getNativeBridgeFunction('pushUserDetails')) {
        getNativeBridgeFunction('pushUserDetails')?.(uToken, userID);
      }
    } catch (error) {
      console.log('Error pushing user details to Android', error);
    }
  }

  // ----------------------------------------------------

  const toClassAndSubjectInfo = (
    classInfo: TeachClassSubjects,
    subject: TeachClassSubjects['subjects'][number]
  ): IClassAndSubjectSelected => ({
    classname: classInfo.className,
    section: classInfo.sectionName,
    sectionId: classInfo.sectionId,
    subjectId: subject.subjectId,
    subject: subject.subjectName,
    icon: subject.iconUrl
      ? getMediaBasePath(subject.iconUrl, 'schoolnetStaticAssetBucket')
      : '',
    color: subject.textColor,
    bookId: subject.bookId,
    classId: classInfo.classId,
    isClickerSupported: !!classInfo.isClickerSupported,
  });

  // ----------------------------------------------------

  const handlePostLoginNavigation = (userData?: TeacherLoginResponseType) => {
    const ssoData = {
      session_mode: (mode as 'teach' | 'assess') || undefined,
      is_sso_user: true,
      sso_subject_ids: [] as number[],
    };

    const qrId = getLocalStorage('qrId');
    if (qrId) {
      dispatch(setSsoData(ssoData));
      navigate(`${SCREEN_RESOLVER}?QRId=${qrId}`);
      return;
    }

    if (subjectEnum) {
      const subjectEnums = subjectEnum
        .split(',')
        .map((val) => getSubjectEnumFromStringEnum(val))
        .filter((val) => !!val);
      const selectedSubjects =
        subjectEnums.length > 0
          ? subjectEnums
              .map((enumVal) => {
                for (const classInfo of userData?.teachClassSubjects || []) {
                  const subject = classInfo.subjects.find(
                    (sub) => sub.subjectEnum === enumVal
                  );
                  if (subject) {
                    return { classInfo, subject };
                  }
                }
                return undefined;
              })
              .filter((val) => !!val)
          : undefined;
      const subIds = (selectedSubjects?.map((val) => val?.subject.subjectId) ||
        []) as number[];
      ssoData.sso_subject_ids = subIds;
      dispatch(setSsoData(ssoData));
      if (subIds.length !== 1) {
        dispatch(setClassAndSubjectInfo(undefined));
        navigate(HOME, {
          state: {
            from: location.pathname,
          },
        });
      } else {
        const match = selectedSubjects?.[0];
        if (match) {
          dispatch(
            setClassAndSubjectInfo(
              toClassAndSubjectInfo(match.classInfo, match.subject)
            )
          );
        }
        const subId = subIds[0];
        if (mode === 'teach') {
          navigate(`${TEACH_CHAPTER_SELECTION}/${subId}`);
        } else if (mode === 'assess') {
          navigate(HOMEWORK_MANAGE);
        } else {
          navigate(HOME);
        }
      }
      return;
    }
    dispatch(setSsoData(ssoData));
    navigate(HOME);
  };

  // ----------------------------------------------------

  const geneoSsoLogin = async () => {
    try {
      if (
        !external_client ||
        !['edunura', 'schoolnet-cl', 'vk12'].includes(external_client)
      ) {
        setErrorMessage('Invalid sso client');
        return;
      }
      const clientDetails = {
        edunura: {
          origin: 'EDUNURA',
          organizationCode: 'EDUNURA-001',
        },
        'schoolnet-cl': {
          origin: 'SCHOOLNET-CL',
          organizationCode: 'SCHOOLNET-CL-001',
        },
        // TEMPORARY: mock provider for VK12's incoming ERP partner (MCB
        // replacement) — swap origin/organizationCode for the real vendor
        // once confirmed. See sso-vendor-integration-guide-template.md.
        vk12: {
          origin: 'SCHOOLCANVAS',
          organizationCode: '1300',
        },
      } as const;
      type ExternalClientKey = keyof typeof clientDetails;
      const clientKey = external_client as ExternalClientKey;
      const selectedClient = clientDetails[clientKey];

      if (uid && token) {
        const res =
          await UmsSsoAPIServiceV1ClientWithStatusCodeHandler.validateUserSsoLoginV4(
            {
              role: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
              requestOrigin: selectedClient.origin,
              token,
              uniqueId: uid,
              organizationCode: selectedClient.organizationCode,
              tenantId,
            }
          );
        const userId = res.userInfo?.profileId;
        const ssoRedirectionUrl = res.ssoRedirectionUrl;

        const resSession =
          await UmsSsoAPIServiceV1ClientWithStatusCodeHandler.createSsoLoginSession(
            {
              profileId: userId,
            }
          );
        const response =
          await UMSLoginAPIServiceV1ClientWithStatusCodeHandler.fetchTeacherProfile(
            {
              teacherProfileId: userId,
            }
          );
        const userData = response.data;

        if (auth && userId) {
          if (userId !== user_info?.teacherProfileId) {
            await handleLogout();
          } else {
            handlePostLoginNavigation(userData);
            return;
          }
        }

        if (ssoRedirectionUrl) {
          dispatch(setSsoRedirectionUrl(ssoRedirectionUrl));
        }
        if (resSession.activeSessionId) {
          localStorage.setItem(
            'activeSession',
            resSession.activeSessionId.toString()
          );
        }

        if (!userData) {
          console.log('User info not present');
          return;
        }
        dispatch(setUserInfo(userData));
        dispatch(setIsMCBUser(true));
        const accessKey = userData.schoolDetails[0]?.schoolId.toString()
          ? 'school-' + userData.schoolDetails[0].schoolId.toString()
          : 'geneo';
        await storeOfflineAccessKeyInCache(accessKey);
        setLocalStorage('userId', userData.teacherProfileId.toString());
        setLocalStorage('role', 'teacher');
        setLocalStorage('auth', 'true');
        setTimeout(async () => {
          await interactionEvent({
            url: 'teacher_login',
            context: 'sso_login',
            name: 'Login',
            isOffline: isOffline,
          });
        }, 3000);
        await aiLoginEvent({
          teacherId: Number(userData.teacherProfileId),
          sessionId: resSession.activeSessionId.toString(),
          isOffline: isOffline,
        });
        pushUserDetailsToAndroid(
          userData.token,
          userData.teacherProfileId.toString()
        );

        handlePostLoginNavigation(userData);
        if (deviceType !== 'android') {
          await requestPermission();
        }
      }
    } catch (err: any) {
      if (err instanceof ConnectError) {
        setErrorMessage(err.message.replace(/.*\]\s*/, '').trim());
        return;
      }
      setErrorMessage('Internal Error');
    }
  };

  // ----------------------------------------------------

  useEffect(() => {
    geneoSsoLogin();
  }, [external_client, uid, token]);

  // ----------------------------------------------------

  if (errorMessage || !(uid && token)) {
    return (
      <MCBErrorScreen
        errorMessage={errorMessage || 'Invalid unique id or token'}
        isCustomError={true}
      />
    );
  }
  return <Loader />;
};
